/**
 * useSearchFilters Hook
 *
 * Manages search filter state synchronized with URL params.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface GuestCount {
  adults: number;
  children: number;
  infants: number;
}

export interface SearchFilters {
  location?: string;
  locationSlug?: string;
  checkIn?: string;
  checkOut?: string;
  guests: GuestCount;
  priceMin?: number;
  priceMax?: number;
  propertyTypes: string[];
  amenities: string[];
  bedrooms?: number;
  bathrooms?: number;
  instantBooking?: boolean;
  petFriendly?: boolean;
}

export interface SearchSort {
  field: 'price' | 'rating' | 'createdAt';
  direction: 'asc' | 'desc';
}

const DEFAULT_FILTERS: SearchFilters = {
  guests: { adults: 1, children: 0, infants: 0 },
  propertyTypes: [],
  amenities: [],
};

export function useSearchFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<SearchFilters>(() => {
    return {
      location:
        searchParams.get('location') ||
        searchParams.get('q') ||
        searchParams.get('area') ||
        undefined,
      locationSlug: searchParams.get('locationSlug') || undefined,
      checkIn: searchParams.get('checkIn') || undefined,
      checkOut: searchParams.get('checkOut') || undefined,
      guests: {
        adults: parseInt(searchParams.get('guests') || searchParams.get('adults') || '1', 10),
        children: parseInt(searchParams.get('children') || '0', 10),
        infants: parseInt(searchParams.get('infants') || '0', 10),
      },
      priceMin: searchParams.get('priceMin')
        ? parseInt(searchParams.get('priceMin')!, 10)
        : undefined,
      priceMax: searchParams.get('priceMax')
        ? parseInt(searchParams.get('priceMax')!, 10)
        : undefined,
      propertyTypes: searchParams.getAll('type'),
      amenities: searchParams.getAll('amenity'),
      bedrooms: searchParams.get('bedrooms')
        ? parseInt(searchParams.get('bedrooms')!, 10)
        : undefined,
      bathrooms: searchParams.get('bathrooms')
        ? parseInt(searchParams.get('bathrooms')!, 10)
        : undefined,
      instantBooking: searchParams.get('instant') === 'true',
      petFriendly: searchParams.get('pets') === 'true',
    };
  }, [searchParams]);

  const sort = useMemo<SearchSort>(() => {
    const sortParam = searchParams.get('sort') || 'recommended';
    switch (sortParam) {
      case 'price_asc':
        return { field: 'price', direction: 'asc' };
      case 'price_desc':
        return { field: 'price', direction: 'desc' };
      case 'rating':
        return { field: 'rating', direction: 'desc' };
      case 'newest':
        return { field: 'createdAt', direction: 'desc' };
      default:
        return { field: 'createdAt', direction: 'desc' };
    }
  }, [searchParams]);

  const setFilters = useCallback(
    (newFilters: Partial<SearchFilters>) => {
      const updated = { ...filters, ...newFilters };
      const params = new URLSearchParams();

      if (updated.location) params.set('location', updated.location);
      if (updated.locationSlug) params.set('locationSlug', updated.locationSlug);
      if (updated.checkIn) params.set('checkIn', updated.checkIn);
      if (updated.checkOut) params.set('checkOut', updated.checkOut);
      if (updated.guests.adults > 1) params.set('adults', String(updated.guests.adults));
      if (updated.guests.children > 0) params.set('children', String(updated.guests.children));
      if (updated.guests.infants > 0) params.set('infants', String(updated.guests.infants));
      if (updated.priceMin) params.set('priceMin', String(updated.priceMin));
      if (updated.priceMax) params.set('priceMax', String(updated.priceMax));
      updated.propertyTypes.forEach((type) => params.append('type', type));
      updated.amenities.forEach((amenity) => params.append('amenity', amenity));
      if (updated.bedrooms) params.set('bedrooms', String(updated.bedrooms));
      if (updated.bathrooms) params.set('bathrooms', String(updated.bathrooms));
      if (updated.instantBooking) params.set('instant', 'true');
      if (updated.petFriendly) params.set('pets', 'true');

      // Preserve sort
      const currentSort = searchParams.get('sort');
      if (currentSort) params.set('sort', currentSort);

      setSearchParams(params);
    },
    [filters, searchParams, setSearchParams]
  );

  const setSort = useCallback(
    (newSort: string) => {
      const params = new URLSearchParams(searchParams);
      if (newSort === 'recommended') {
        params.delete('sort');
      } else {
        params.set('sort', newSort);
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.priceMin !== undefined ||
      filters.priceMax !== undefined ||
      filters.propertyTypes.length > 0 ||
      filters.amenities.length > 0 ||
      filters.bedrooms !== undefined ||
      filters.bathrooms !== undefined ||
      filters.instantBooking ||
      filters.petFriendly
    );
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.priceMin !== undefined || filters.priceMax !== undefined) count++;
    count += filters.propertyTypes.length;
    count += filters.amenities.length;
    if (filters.bedrooms !== undefined) count++;
    if (filters.bathrooms !== undefined) count++;
    if (filters.instantBooking) count++;
    if (filters.petFriendly) count++;
    return count;
  }, [filters]);

  return {
    filters,
    sort,
    setFilters,
    setSort,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
    DEFAULT_FILTERS,
  };
}

export default useSearchFilters;
